import { IntlShape } from 'react-intl';

import { baselineClasses, glucoseClasses } from 'constants/constants';

import { round } from './Graphs';
import { convertGlucoseUnit } from './Measurements';
import {
  getDecimals,
  getUnitForMeasurementClass,
  normalizeUnitKey
} from './MeasurementsStoreFunctions';

export const PRESSURE_BASELINE_CLASS = 'pressure';
export const pressureBaselineParts: IBaselinePartKey[] = ['systolic', 'diastolic'];

export interface IBaselinePart {
  className: string;
  range: IBaselineRange;
}

export const isPressurePart = (className: string): className is IBaselinePartKey =>
  pressureBaselineParts.includes(className as IBaselinePartKey);

export const getBaselinePartClasses = (className: string): string[] => {
  return className === PRESSURE_BASELINE_CLASS ? pressureBaselineParts : [className];
};

export const getBaselineClass = (partClass: string) => {
  return isPressurePart(partClass) ? PRESSURE_BASELINE_CLASS : partClass;
};

export const getBaselineUnitClass = (className: string) => {
  if (className === PRESSURE_BASELINE_CLASS) return 'systolic';
  return className === 'dry_weight' ? 'weight' : className;
};

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== '';

export const getBaselineParts = (baseline: IBaselineValues): IBaselinePart[] => {
  if (baseline.class === PRESSURE_BASELINE_CLASS) {
    return pressureBaselineParts.reduce<IBaselinePart[]>((parts, part) => {
      const range = baseline[part];
      if (range && hasValue(range.low) && hasValue(range.high)) {
        parts.push({
          className: part,
          range: { low: Number(range.low), high: Number(range.high) }
        });
      }
      return parts;
    }, []);
  }
  if (!hasValue(baseline.low) || !hasValue(baseline.high)) return [];
  return [
    { className: baseline.class, range: { low: Number(baseline.low), high: Number(baseline.high) } }
  ];
};

export const convertBaselineValue = (
  value: number,
  className: string,
  sourceUnit: string,
  targetUnit: string
): number | undefined => {
  const source = normalizeUnitKey(sourceUnit);
  const target = normalizeUnitKey(targetUnit);
  if (source === target) return value;
  return glucoseClasses.includes(className as IMeasurementClassType)
    ? convertGlucoseUnit(value, source as IGlucoseUnit, target as IGlucoseUnit)
    : undefined;
};

export const getBaselineRangeForClass = (
  baselines: IBaselineValues[],
  className: string,
  unit?: string
): IBaselineRange | undefined => {
  const baselineClass = getBaselineClass(className);
  const baseline = baselines.find((item) => item.class === baselineClass);
  const part = baseline && getBaselineParts(baseline).find((x) => x.className === className);
  if (!baseline || !part) return undefined;
  const targetUnit = unit || baseline.unit;
  const decimals = getDecimals(className, targetUnit);
  const low = convertBaselineValue(part.range.low, className, baseline.unit, targetUnit);
  const high = convertBaselineValue(part.range.high, className, baseline.unit, targetUnit);
  if (low === undefined || high === undefined) return undefined;
  return { low: round(low, decimals), high: round(high, decimals) };
};

// Sleep readings show as hours and minutes with no unit, but a baseline is entered in decimal hours.
export const getBaselineUnitLabel = (className: string, unit: string, intl: IntlShape) => {
  if (className === 'sleep') return intl.formatMessage({ id: 'hours' });
  return getUnitForMeasurementClass(className, unit, intl);
};

export const formatBaselineValue = (value: number, className: string, unit: string) =>
  `${round(Number(value), getDecimals(className, unit))}`;

export const formatBaselineRange = (range: IBaselineRange, className: string, unit: string) =>
  `${formatBaselineValue(range.low, className, unit)}–${formatBaselineValue(
    range.high,
    className,
    unit
  )}`;

export const getBaselineOffset = (
  value: number,
  range: IBaselineRange,
  className: string,
  unit: string
) => {
  const decimals = getDecimals(className, unit);
  const reading = round(value, decimals);
  if (reading > range.high) return round(reading - range.high, decimals);
  if (reading < range.low) return round(reading - range.low, decimals);
  return 0;
};

export const formatBaselineOffset = (offset: number) => {
  if (offset === 0) return '0';
  return offset > 0 ? `+${offset}` : `−${Math.abs(offset)}`;
};

export const isInBaseline = (
  value: number,
  range: IBaselineRange,
  className: string,
  unit: string
) => getBaselineOffset(value, range, className, unit) === 0;

export const sortBaselines = <T extends IBaselineValues>(baselines: T[]) =>
  [...baselines].sort(
    (a, b) => baselineClasses.indexOf(a.class) - baselineClasses.indexOf(b.class)
  );
